import { isValidElement, type ReactNode } from 'react';
import { tamanhoDoNumero } from './tamanho-do-numero';
import { NumeroAnimado } from './NumeroAnimado';
import { textoFinalDoNumero } from './texto-final-do-numero';
import { HelpTip } from './HelpTip';
import { cn } from '@/lib/cn';

/**
 * KpiCard — card de indicador com slots FIXOS (FASE-B design system).
 *
 * Anatomia — os px são os REAIS da escala rebaseada (`tailwind.config.ts`),
 * não os da maquete original. A doc anterior dizia 12px/24px e estava mentindo
 * sobre o próprio componente desde que a escala subiu pra piso 13:
 *
 *   label    — Inter 600 **13px** caps (ink-**2**)   ← identidade do número
 *   value    — SF Mono 500 **22px → 27px** tabular (ink-1)  ← o protagonista
 *   context  — Inter 500 **13px** (ink-3)            ← rodapé; aceita link
 *
 * ── Por que a cor do `label` mudou de ink-3 pra ink-2
 *
 * Medido em `/dev/ui`: `label` e `context` saíam com o MESMO tamanho (13px) e a
 * MESMA cor (ink-3, `rgb(111,103,92)`), separados só por peso (600 vs 500) — o
 * que a 13px, um em caixa alta e outro não, praticamente não se lê como
 * hierarquia. Na prática o card tinha DOIS níveis, não três: o número, e "todo
 * o resto". O rótulo que diz *o que o número é* ficava igual ao rodapé.
 *
 * A correção não pôde vir do tamanho: **13 é o piso** da escala aprovada, então
 * não existe degrau abaixo dele pro `context` descer. O terceiro nível veio da
 * cor — value (ink-1) > label (ink-2) > context (ink-3) —, que é a mesma escada
 * que o resto do sistema já usa.
 *
 * Isto é regra de COMPONENTE: muda aqui e vale nos 21 lugares que montam KpiCard.
 * Ninguém deve ajustar hierarquia de KPI tela a tela.
 *
 * A altura mínima é fixa (min-h-[118px]) pra fileira inteira ficar igual —
 * mata o "quadros um maior que o outro" (dor 6 do diagnóstico). Monte a
 * fileira com grid (`grid grid-cols-2 lg:grid-cols-4 gap-4`).
 *
 * <KpiCard label="Propostas ganhas" value="12" context="6 abertas no funil" />
 * <KpiCard label="Faturamento" value="R$ 0" context={<>sem meta · <Link to="/admin/comercial/metas">definir em Metas</Link></>} />
 */
interface KpiCardProps {
  label: string;
  /**
   * Texto do "?" ao lado do rótulo — a regra do projeto pede tooltip em toda
   * função, e até aqui os KPIs só tinham o `context` (que é frase de rodapé,
   * não explicação sob demanda).
   *
   * ⚠️ ADITIVO: sem `help`, o card renderiza exatamente como antes. Nenhum
   * KPI existente muda de comportamento — por isso não houve varredura de
   * irmãos nesta rodada.
   */
  help?: string;
  value: ReactNode;
  /** Sufixo pequeno colado ao valor (ex.: "/mês"). */
  valueSuffix?: string;
  context?: ReactNode;
  /**
   * Slot livre no rodapé (abaixo do contexto) pra miniconteúdo que não é
   * texto — ex.: a sparkline da Margem de lucro no Financeiro. É um <div>
   * de propósito (sparklines renderizam <div>/<svg>, inválidos num <span>).
   */
  footer?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

/**
 * O que o valor vai ESCREVER, para escolher a fonte. Texto e número: ele mesmo. `<NumeroAnimado>`: o texto final
 * da contagem (antes media "[object Object]", 15 caracteres — moeda cortada em 768 px, inteiro pequeno encolhido
 * sem precisar). Outro elemento: como antes.
 */
function textoParaMedir(value: ReactNode): string {
  if (isValidElement<{ valor: number; formatar?: (n: number) => string }>(value) && value.type === NumeroAnimado) {
    return textoFinalDoNumero(value.props);
  }
  return String(value ?? '');
}

export function KpiCard({ label, help, value, valueSuffix, context, footer, className, ...props }: KpiCardProps) {
  return (
    <div
      className={cn(
        'flex min-h-[118px] min-w-0 flex-col gap-1.5 rounded-xl border border-line dark:border-line-dark',
        'bg-paper-surface dark:bg-paper-dark-surface shadow-card p-4 md:p-5',
        className
      )}
      {...props}
    >
      {/* `data-texto-inteiro`: o `scripts/texto-inteiro.mjs` confere que o rótulo cabe numa linha (V5, 06/10/2026).
          ⚠️ Só a marca — nada muda na tela. Rótulo que quebra se conserta no TEXTO de quem monta o cartão. */}
      <span
        data-texto-inteiro
        className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-2 dark:text-ink-dark-2"
      >
        {label}
        {help && <HelpTip texto={help} />}
      </span>
      {/*
        🔴 ITEM 27 (25/09/2026) — O VALOR NUNCA ESTOURA O CARTÃO, seja ele qual for.
        A cura mora AQUI, no componente, e não no cartão que reclamou: a mesma
        fileira tem outros quatro valores longos esperando a vez, e os 21 lugares
        que montam KpiCard herdam a regra de graça.

        ⚠️ A tentativa anterior — baixar de 24px para 20px no mobile — tratou o
        SINTOMA de UM valor. Ela não é uma garantia: basta o valor crescer um
        dígito e o estouro volta. **Medido no beta em 25/09/2026**, "Mensalidades
        novas" de setembro/2026 vale `R$ 18.203,11/mês` (17 caracteres), contra
        os 13 de `R$ 244.067,30` que motivaram aquele ajuste.

        ⛔ **A CURA DE 25/09 VIROU A CAUSA, e ela era minha** (M2, 28/09/2026).
        Ela era `min-w-0` + `[overflow-wrap:anywhere]`, e resolveu o problema
        daquele dia: o número empurrava a página para fora da viewport.
        🔴 **Mas `anywhere` autoriza a quebra em QUALQUER ponto — inclusive
        entre o `R$` e o número.** Medido no beta: `R$ 28.398,09/mês` ocupava
        **duas linhas**, partido depois do cifrão.

        ⚠️ **E o teste não pegou porque usava o valor mais LONGO medido**
        (`R$ 18.203,11/mês`), que cabia. 🔴 **O teste certo é a pior RAZÃO entre
        o valor e a largura da coluna**, não o valor mais longo: a coluna mais
        estreita do Resumo tem 242 px com quatro cartões.

        ✅ **Agora ele ENCOLHE em vez de quebrar:** uma linha só
        (`whitespace-nowrap`) e a fonte por degraus (`tamanhoDoNumero`), pelo
        comprimento do que vai ser escrito — sufixo incluído, porque ele divide
        a linha.

        ✅ **O `min-w-0` FICA**: ele é o que impede a célula do grid de empurrar
        a página, e esse problema era real. ❌ O que saiu foi só o `anywhere`.
      */}
      <span
        className={cn(
          'block min-w-0 font-mono font-medium tabular-nums leading-tight whitespace-nowrap',
          'text-ink-1 dark:text-ink-dark-1',
          tamanhoDoNumero(textoParaMedir(value), valueSuffix ? String(valueSuffix) : undefined)
        )}
      >
        {value}
        {valueSuffix && (
          <span className="font-body text-sm font-medium text-ink-3 dark:text-ink-dark-3 whitespace-nowrap">
            {valueSuffix}
          </span>
        )}
      </span>
      {context && (
        <span className="mt-auto text-xs font-medium text-ink-3 dark:text-ink-dark-3 [&_a]:font-semibold [&_a]:text-red-deep dark:[&_a]:text-red-glow [&_a:hover]:underline">
          {context}
        </span>
      )}
      {footer && <div className={cn('w-full', context ? 'mt-1.5' : 'mt-auto')}>{footer}</div>}
    </div>
  );
}
